import { SiteStore } from '~/stores/site'
import type { IPageNode, WaypointNode } from '~/models/journey/waypoints'
import type { Path } from '~/types/util'
import {
  SignInResult,
  SignUpResult,
  VerifyResult,
  WaypointAuth,
  WaypointResult
} from '~/generated/api-clients-generated'
import { ProfileStore } from '~/stores/profile'
import { SessionStore } from '~/stores/session'

export class Auth {
  static use() {
    const { auth: cookie, touch } = CookieJar.use()
    touch()

    const isAuthenticated = computed(() => !_isNil(cookie.value))

    async function signUp(
      soft: boolean = false,
      req: Record<string, string> | null = null,
      source: string | null = null,
      additionalAnalyticsData: Record<string, unknown> | null = null
    ) {
      await ProfileStore.createIfEmpty()

      const profile = ProfileStore.use()
      const session = SessionStore.use()

      const { createAccountClient } = PxApi.use()
      const client = createAccountClient()

      const request = req ?? {
        firstName: session.authBag.firstName,
        lastName: session.authBag.lastName,
        birthDate: session.authBag.birthdate,
        registrationToken: session.authBag.registrationToken,
        profileId: profile.id ?? session.profileId,
        identifier: session.authBag.identifier,
        analyticsData: {
          site: 'px',
          source,
          whatBringsYouIn: session.whatBringsYouIn?.selection ?? null,
          ...additionalAnalyticsData
        }
      }
      console.log('REQUEST', request)
      try {
        const response = soft ? await client.signUpSoft(request) : await client.signUp(request)

        if (response.result === SignUpResult.Success) {
          session.authBag.registrationToken = response.token
        }

        return response
      } catch (ex) {
        return {
          result: SignUpResult.Error
        }
      }
    }

    async function signIn() {
      const session = SessionStore.use()

      const { createAccountClient } = PxApi.use()
      const client = createAccountClient()

      try {
        const response = await client.signIn({
          identifier: session.authBag.identifier
        })

        session.authBag.showTcpaConsent = response.showTcpaConsent
        return response
      } catch (ex) {
        return {
          result: SignInResult.Error
        }
      }
    }

    async function signInSoft(profileId: string) {
      const session = SessionStore.use()

      const { createAccountClient } = PxApi.use()
      const client = createAccountClient()

      try {
        const response = await client.signInSoft(profileId)

        session.authBag.showTcpaConsent = response.showTcpaConsent
        return response
      } catch (ex) {
        return {
          result: SignInResult.Error
        }
      }
    }

    async function verify(code: string, source?: string | null) {
      const session = SessionStore.use()

      const { createAccountClient } = PxApi.use()
      const client = createAccountClient()

      try {
        return await client.verify({
          code: code,
          identifier: session.authBag.identifier,
          registrationToken: session.authBag.registrationToken,
          profileId: session.profileId!,
          attribution: !_isEmpty(session.params) ? { params: session.params } : null,
          analyticsData: {
            site: 'px',
            source
          }
        })
      } catch (ex) {
        return VerifyResult.Error
      }
    }

    async function verifyMagicLink(code: string) {
      const session = SessionStore.use()

      const { createAccountClient } = PxApi.use()
      const client = createAccountClient()

      try {
        return await client.magicLink({
          code: code,
          profileId: session.profileId!
        })
      } catch (ex) {
        return VerifyResult.Error
      }
    }

    async function signOut() {
      console.log('SIGNING OUT')
      const { createAccountClient } = PxApi.use()
      const client = createAccountClient()

      await client.logOut()
    }

    async function signUpRedirect(
      returnTo: WaypointNode | Path | null = null,
      isWaypoint: boolean = false
    ) {
      const site = SiteStore.use()
      return authRedirect(site.journey.signUpPath!, returnTo, isWaypoint)
    }

    async function signInRedirect(
      returnTo: WaypointNode | Path | null = null,
      isWaypoint: boolean = false
    ) {
      const site = SiteStore.use()
      return authRedirect(site.journey.signInPath!, returnTo, isWaypoint)
    }

    async function authRedirect(
      to: string,
      returnTo: WaypointNode | Path | null,
      isWaypoint: boolean = false
    ) {
      const { push, replace } = AppRouter.use()
      const { path, route } = AppRoute.use()
      const { current } = Waypoint.use()

      const returnPath = typeof returnTo === 'string' ? returnTo : (returnTo as IPageNode)?.path

      const nextPath = route.query.next?.toString() || null
      const key = isWaypoint || !!nextPath ? 'next' : 'return_to'

      const location = {
        path: to,
        query: {
          [key]: returnPath ?? nextPath ?? (route.query.return_to?.toString() || null) ?? path.value
        }
      }

      if (current.value?.replace) {
        await replace(location)
      } else {
        await push(location)
      }
    }

    async function returnRedirect() {
      const site = SiteStore.use()
      const { replace } = AppRouter.use()
      const { route } = AppRoute.use()
      const { current } = Waypoint.use()
      const { getCourse } = WaypointAutoPilot.use(WaypointResult.Next)

      const next = route.query.next?.toString()
      const returnTo = route.query.return_to?.toString()

      const path =
        (next || null) ?? // from RedirectNode
        getCourse() ?? // Sitemap config
        (current.value?.path === site.journey.signInPath ? site.journey.accountHomePath : null) ?? // /account/sign-in -> /dashboard
        (returnTo || null) ?? // /account/sign-up -> origin page
        '/' // ¯\_(ツ)_/¯

      // console.log('== RETURN REDIRECT ==')
      // console.log('Next Param: ', next)
      // console.log('Return To Param: ', returnTo)
      // console.log('Course: ', getCourse())
      // console.log('Current waypoint: ', current.value)
      // console.log('Next waypoint: ', current.value?.getNext(WaypointResult.Next) as IPageNode)
      // console.log('=====================')

      await replace(path)
    }

    function routeGuard(to: string) {
      const { get } = Waypoint.use()
      const site = SiteStore.use()

      const waypoint = get(to)

      if (waypoint?.auth === WaypointAuth.Protected && !isAuthenticated.value) {
        if (normalizePath(to) !== site.journey.signInPath) {
          return navigateTo(site.journey.signInPath)
        }
      }

      if (waypoint?.auth === WaypointAuth.Guest && isAuthenticated.value) {
        if (normalizePath(to) !== site.journey.accountHomePath) {
          return navigateTo(site.journey.accountHomePath)
        }
      }
    }

    return {
      isAuthenticated,
      signUp,
      signIn,
      signInSoft,
      verify,
      verifyMagicLink,
      signOut,
      signUpRedirect,
      signInRedirect,
      returnRedirect,
      routeGuard
    }
  }
}
